import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { EquityCurveResponse, EquityLatestResponse } from '../types/api';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, Line, ComposedChart } from 'recharts';
import { RefreshCw, TrendingUp, DollarSign, Percent, Camera, ArrowUpRight, ArrowDownRight, Calendar, BarChart3 } from 'lucide-react';
import { formatShortDateEastern, formatDateTimeEastern } from '../lib/dateUtils';

export function EquityCurve() {
  const [curve, setCurve] = useState<EquityCurveResponse | null>(null);
  const [latest, setLatest] = useState<EquityLatestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCash, setShowCash] = useState(false);
  const [range, setRange] = useState('30');
  const [chartMode, setChartMode] = useState<'dollars' | 'roi'>('dollars');

  const loadData = async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(range) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const [curveData, latestData] = await Promise.all([
        api.getEquityCurve(startDate, endDate),
        api.getEquityLatest(),
      ]);
      setCurve(curveData);
      setLatest(latestData);
    } catch (error) {
      console.error('Error loading equity curve:', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadData();
    
    const interval = setInterval(() => {
      api.getEquityLatest().then(setLatest);
    }, 60000);
    
    return () => clearInterval(interval);
  }, [range]);

  const handleTakeSnapshot = async () => {
    try {
      await api.takeSnapshot();
      await loadData();
    } catch (error) {
      console.error('Error taking snapshot:', error);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const spyByDate: Record<string, { price: number; return_pct: number }> = {};
  if (curve?.spy_benchmark) {
    for (const pt of curve.spy_benchmark) {
      const d = formatShortDateEastern(pt.timestamp);
      spyByDate[d] = { price: pt.price, return_pct: pt.return_pct };
    }
  }

  const rawChartData = curve?.points.map((point) => {
    const dateStr = formatShortDateEastern(point.timestamp);
    const spy = spyByDate[dateStr];
    return {
      date: dateStr,
      timestamp: point.timestamp,
      equity: point.equity,
      cash: point.cash,
      positions: point.positions_value,
      spyPrice: spy?.price,
      spyReturnPct: spy?.return_pct,
    };
  }) || [];

  const baseEquity = rawChartData.length > 0 ? rawChartData[0].equity : 1;
  const baseCash = rawChartData.length > 0 ? rawChartData[0].cash : 1;
  const baseSpyPrice = rawChartData.length > 0 ? rawChartData[0].spyPrice : undefined;

  const [showSpy, setShowSpy] = useState(true);

  const chartData = chartMode === 'roi'
    ? rawChartData.map(d => ({
        ...d,
        equity: baseEquity && baseEquity > 0 ? ((d.equity - baseEquity) / baseEquity) * 100 : 0,
        cash: baseCash && baseCash > 0 ? (((d.cash ?? 0) - baseCash) / baseCash) * 100 : 0,
        spyValue: d.spyReturnPct,
      }))
    : rawChartData.map(d => ({
        ...d,
        spyValue: d.spyPrice != null && baseSpyPrice != null && baseSpyPrice > 0 && baseEquity > 0
          ? (d.spyPrice / baseSpyPrice) * baseEquity
          : undefined,
      }));

  const yAxisDomain = (() => {
    if (chartData.length === 0) return [0, 'auto'] as [number, string];
    const equityValues = chartData
      .map(d => showCash ? Math.max(d.equity ?? 0, d.cash ?? 0) : (d.equity ?? 0))
      .filter((v): v is number => v != null);
    const spyValues = showSpy ? chartData
      .map(d => d.spyValue)
      .filter((v): v is number => v != null) : [];
    const values = [...equityValues, ...spyValues];
    if (values.length === 0) return [0, 'auto'] as [number, string];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1 || (Math.abs(max) * 0.05) || 1;
    if (chartMode === 'roi') {
      return [min - padding, max + padding] as [number, number];
    }
    return [Math.max(0, min - padding), max + padding] as [number, number];
  })();

  const dailyChange = latest?.daily_change ?? 0;
  const dailyChangePct = latest?.daily_change_pct ?? 0;
  const isPositive = dailyChange >= 0;

  const cumulativeReturn = (() => {
    if (rawChartData.length < 2) return null;
    const first = rawChartData[0].equity;
    const last = rawChartData[rawChartData.length - 1].equity;
    if (!first || first === 0) return null;
    return ((last - first) / first) * 100;
  })();

  const annualizedReturn = (() => {
    if (rawChartData.length < 2 || cumulativeReturn === null) return null;
    const firstTs = new Date(rawChartData[0].timestamp).getTime();
    const lastTs = new Date(rawChartData[rawChartData.length - 1].timestamp).getTime();
    const days = (lastTs - firstTs) / (1000 * 60 * 60 * 24);
    if (days < 1) return null;
    const first = rawChartData[0].equity;
    const last = rawChartData[rawChartData.length - 1].equity;
    if (!first || first === 0) return null;
    return (Math.pow(last / first, 365 / days) - 1) * 100;
  })();

  const isCumulativePositive = (cumulativeReturn ?? 0) >= 0;
  const isAnnualizedPositive = (annualizedReturn ?? 0) >= 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading equity curve...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Equity Curve</h1>
          <p className="text-gray-600 mt-1 text-base">Track your portfolio performance over time</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="px-5 py-3 text-base border-2 border-gray-300 rounded-xl bg-white text-gray-700 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-600/50 focus:border-emerald-600 cursor-pointer hover:border-emerald-400 transition-all"
          >
            <option value="3">3 Days</option>
            <option value="7">7 Days</option>
            <option value="30">30 Days</option>
            <option value="90">90 Days</option>
            <option value="365">1 Year</option>
          </select>
          <button
            onClick={() => setShowCash(!showCash)}
            className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold rounded-xl transition-all  ${
              showCash 
                ? 'bg-emerald-700 text-white shadow-md' 
                : 'bg-white text-gray-700 border-2 border-slate-200 shadow-sm hover:border-emerald-400'
            }`}
          >
            {showCash ? 'Hide Cash' : 'Show Cash'}
          </button>
          <button
            onClick={() => setShowSpy(!showSpy)}
            className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold rounded-xl transition-all ${
              showSpy 
                ? 'bg-orange-600 text-white shadow-md' 
                : 'bg-white text-gray-700 border-2 border-slate-200 shadow-sm hover:border-orange-400'
            }`}
          >
            {showSpy ? 'Hide SPY' : 'Show SPY'}
          </button>
          <button onClick={handleTakeSnapshot} className="inline-flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold rounded-xl bg-emerald-700 text-white shadow-md hover:bg-emerald-800  transition-all">
            <Camera className="w-5 h-5" />
            Take Snapshot
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-300 hover:shadow-md hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Current Equity</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {latest ? formatCurrency(latest.equity) : '--'}
              </p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-emerald-700 shadow-md">
              <DollarSign className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-300 hover:shadow-md hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Daily Change</p>
              <div className="flex items-center gap-2 mt-2">
                <p className={`text-3xl font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                  {latest?.daily_change != null
                    ? `${dailyChange >= 0 ? '+' : ''}${formatCurrency(dailyChange)}`
                    : '--'
                  }
                </p>
                {latest?.daily_change != null && (
                  isPositive 
                    ? <ArrowUpRight className="w-6 h-6 text-emerald-500" />
                    : <ArrowDownRight className="w-6 h-6 text-red-500" />
                )}
              </div>
            </div>
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${isPositive ? 'bg-emerald-600' : 'bg-red-600'}`}>
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-300 hover:shadow-md hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Daily Change %</p>
              <div className="flex items-center gap-2 mt-2">
                <p className={`text-3xl font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                  {latest?.daily_change_pct != null
                    ? `${dailyChangePct >= 0 ? '+' : ''}${dailyChangePct.toFixed(2)}%`
                    : '--'
                  }
                </p>
              </div>
            </div>
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${isPositive ? 'bg-emerald-600' : 'bg-red-600'}`}>
              <Percent className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-300 hover:shadow-md hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Cumulative Return</p>
              <div className="flex items-center gap-2 mt-2">
                <p className={`text-3xl font-bold ${cumulativeReturn == null ? 'text-gray-900' : isCumulativePositive ? 'text-emerald-600' : 'text-red-600'}`}>
                  {cumulativeReturn != null
                    ? `${cumulativeReturn >= 0 ? '+' : ''}${cumulativeReturn.toFixed(2)}%`
                    : '--'
                  }
                </p>
              </div>
            </div>
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${cumulativeReturn == null ? 'bg-gray-400' : isCumulativePositive ? 'bg-emerald-600' : 'bg-red-600'}`}>
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-300 hover:shadow-md hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Annualized Return</p>
              <div className="flex items-center gap-2 mt-2">
                <p className={`text-3xl font-bold ${annualizedReturn == null ? 'text-gray-900' : isAnnualizedPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                  {annualizedReturn != null
                    ? `${annualizedReturn >= 0 ? '+' : ''}${annualizedReturn.toFixed(2)}%`
                    : '--'
                  }
                </p>
              </div>
            </div>
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${annualizedReturn == null ? 'bg-gray-400' : isAnnualizedPositive ? 'bg-emerald-600' : 'bg-red-600'}`}>
              <Calendar className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden ">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Performance Chart</h2>
          <div className="flex bg-gray-200 rounded-lg p-1">
            <button
              onClick={() => setChartMode('dollars')}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                chartMode === 'dollars'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              Dollars
            </button>
            <button
              onClick={() => setChartMode('roi')}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                chartMode === 'roi'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Percent className="w-4 h-4" />
              % ROI
            </button>
          </div>
        </div>
        <div className="p-6">
          {chartData.length === 0 ? (
            <div className="text-center py-16 text-gray-600">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <TrendingUp className="w-10 h-10 text-slate-400" />
              </div>
              <p className="font-semibold text-gray-600 text-lg">No equity data available</p>
              <p className="text-base text-slate-400 mt-1">Take a snapshot to start tracking</p>
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#047857" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#047857" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="colorCash" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" fontSize={13} tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={13}
                    tickLine={false}
                    axisLine={false}
                    domain={yAxisDomain}
                    tickFormatter={(value) => {
                      if (value == null) return '';
                      if (chartMode === 'roi') return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
                      return `$${(value / 1000).toFixed(0)}k`;
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      padding: '14px 18px',
                      fontSize: '15px'
                    }}
                    formatter={(value, name) => {
                      if (value == null) return ['--', ''];
                      const label = name === 'spyValue' ? 'SPY (Buy & Hold)' : name === 'cash' ? 'Cash' : 'Portfolio';
                      if (chartMode === 'roi') {
                        const v = value as number;
                        return [`${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, label];
                      }
                      return [formatCurrency(value as number), label];
                    }}
                    labelFormatter={(label) => String(label)}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '15px', fontWeight: '600' }}
                    formatter={(value) => {
                      if (value === 'spyValue') return 'SPY (Buy & Hold)';
                      if (value === 'equity') return 'Total Equity';
                      if (value === 'cash') return 'Cash';
                      return value;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="equity"
                    name="equity"
                    stroke="#047857"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorEquity)"
                  />
                  {showCash && (
                    <Area
                      type="monotone"
                      dataKey="cash"
                      name="cash"
                      stroke="#10b981"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorCash)"
                    />
                  )}
                  {showSpy && (
                    <Line
                      type="monotone"
                      dataKey="spyValue"
                      name="spyValue"
                      stroke="#ea580c"
                      strokeWidth={2}
                      strokeDasharray="6 3"
                      dot={false}
                      connectNulls
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>


      <div className="text-base text-gray-600 text-center font-medium">
        Last updated: {latest?.timestamp ? formatDateTimeEastern(latest.timestamp) : 'Never'}
      </div>
    </div>
  );
}
